import { useState } from 'react';

const TEMPLATES = [
  {
    id: 'standup',
    name: 'Daily Standup',
    icon: '☀️',
    description: 'Quick 15-min sync that stays on track',
    duration: 15,
    timeSaved: '10 min',
    category: 'efficiency',
    benefits: ['Keeps meetings under 15 min', 'Prevents rambling', 'Everyone gets heard'],
    agenda: [
      'What did you do yesterday?',
      'What will you do today?',
      'Any blockers?'
    ],
    tips: 'Keep it brief. Time-box each person to 2 minutes. Stand up to keep it short!'
  },
  {
    id: 'interview',
    name: 'Job Interview',
    icon: '💼',
    description: 'Professional, structured interview in 60 min',
    duration: 60,
    timeSaved: '15 min',
    category: 'structured',
    benefits: ['Stay professional', 'Cover all topics', 'Leave time for Q&A'],
    agenda: [
      'Introduction (5 min)',
      'Background & Experience (15 min)',
      'Technical Questions (20 min)',
      'Candidate Questions (15 min)',
      'Next Steps (5 min)'
    ],
    tips: 'Prepare questions in advance. Take notes during the call. Watch the clock!'
  },
  {
    id: 'demo',
    name: 'Product Demo',
    icon: '🎯',
    description: 'Engaging 30-min demo that converts',
    duration: 30,
    timeSaved: '20 min',
    category: 'efficiency',
    benefits: ['Maximize impact', 'Leave time for Q&A', 'End with clear next steps'],
    agenda: [
      'Problem Overview (5 min)',
      'Solution Demo (15 min)',
      'Q&A (10 min)'
    ],
    tips: 'Share your screen. Have a backup plan if tech fails. Focus on value, not features.'
  },
  {
    id: 'brainstorm',
    name: 'Brainstorming',
    icon: '💡',
    description: 'Creative 45-min session with actionable outcomes',
    duration: 45,
    timeSaved: '25 min',
    category: 'structured',
    benefits: ['Everyone contributes', 'Capture all ideas', 'Leave with action items'],
    agenda: [
      'Define the problem (5 min)',
      'Silent ideation (10 min)',
      'Share ideas (20 min)',
      'Vote & next steps (10 min)'
    ],
    tips: 'No bad ideas! Encourage wild thinking first, filter later. Use screen share to collaborate.'
  },
  {
    id: 'oneonone',
    name: '1-on-1 Check-in',
    icon: '👥',
    description: 'Meaningful 30-min personal connection',
    duration: 30,
    timeSaved: '15 min',
    category: 'structured',
    benefits: ['Build trust', 'Address concerns', 'Support growth'],
    agenda: [
      'How are you doing? (5 min)',
      'Current projects update (10 min)',
      'Challenges & support (10 min)',
      'Growth & feedback (5 min)'
    ],
    tips: 'Make it a safe space. Listen more than you talk. Take notes on action items.'
  },
  {
    id: 'retrospective',
    name: 'Team Retrospective',
    icon: '🔄',
    description: 'Productive 60-min reflection with clear actions',
    duration: 60,
    timeSaved: '30 min',
    category: 'structured',
    benefits: ['Learn from wins', 'Address issues', 'Commit to improvements'],
    agenda: [
      'Set the stage (5 min)',
      'What went well? (15 min)',
      'What didn\'t go well? (15 min)',
      'Action items (20 min)',
      'Closing (5 min)'
    ],
    tips: 'Focus on learning, not blaming. Commit to specific actions. Follow up on last retrospective.'
  }
];

export default function MeetingTemplates({ onSelectTemplate, onClose }) {
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const handleSelect = (template) => {
    setSelectedTemplate(template);
  };

  const handleUseTemplate = () => {
    if (selectedTemplate && onSelectTemplate) {
      onSelectTemplate(selectedTemplate);
      onClose?.();
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'efficiency':
        return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400';
      case 'structured':
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400';
      default:
        return 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-400';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white dark:bg-[#1c1c1e] rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden border border-[#d2d2d7] dark:border-[#2c2c2e]" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 border-b border-[#d2d2d7] dark:border-[#2c2c2e] bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-3xl font-bold text-[#1d1d1f] dark:text-white mb-2">Meeting Templates</h2>
              <p className="text-base text-[#1d1d1f] dark:text-white font-medium">
                ⏱️ Save time • 🎯 Stay focused • ✅ Get results
              </p>
              <p className="text-sm text-[#86868b] dark:text-[#8e8e93] mt-1">
                Structured agendas that keep your meetings efficient and on track
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/50 dark:hover:bg-[#2c2c2e] rounded-lg transition"
            >
              <svg className="w-6 h-6 text-[#86868b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex h-[calc(90vh-220px)]">
          {/* Template Grid */}
          <div className="w-1/2 border-r border-[#d2d2d7] dark:border-[#2c2c2e] overflow-y-auto p-6">
            <div className="grid grid-cols-1 gap-3">
              {TEMPLATES.map(template => (
                <button
                  key={template.id}
                  onClick={() => handleSelect(template)}
                  className={`text-left p-4 rounded-xl transition-all ${
                    selectedTemplate?.id === template.id
                      ? 'bg-[#06c] text-white shadow-lg scale-[1.02]'
                      : 'bg-[#f5f5f7] dark:bg-[#2c2c2e] hover:bg-[#e8e8ed] dark:hover:bg-[#3c3c3e] text-[#1d1d1f] dark:text-white hover:scale-[1.01]'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className="text-4xl flex-shrink-0">{template.icon}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="font-bold text-lg">{template.name}</div>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          selectedTemplate?.id === template.id
                            ? 'bg-white/20 text-white'
                            : getCategoryColor(template.category)
                        }`}>
                          Saves {template.timeSaved}
                        </span>
                      </div>
                      <div className={`text-sm mb-2 ${selectedTemplate?.id === template.id ? 'text-white/90' : 'text-[#86868b] dark:text-[#8e8e93]'}`}>
                        {template.description}
                      </div>
                      <div className="flex items-center gap-3">
                        <div className={`flex items-center gap-1 text-xs ${selectedTemplate?.id === template.id ? 'text-white/80' : 'text-[#86868b] dark:text-[#8e8e93]'}`}>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {template.duration} min
                        </div>
                        <div className={`flex items-center gap-1 text-xs ${selectedTemplate?.id === template.id ? 'text-white/80' : 'text-[#86868b] dark:text-[#8e8e93]'}`}>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                          {template.agenda.length} steps
                        </div>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Template Details */}
          <div className="flex-1 p-6 overflow-y-auto bg-[#f5f5f7] dark:bg-[#0d0d0d]">
            {selectedTemplate ? (
              <div className="space-y-6">
                {/* Header */}
                <div>
                  <div className="flex items-start gap-4 mb-4">
                    <div className="text-6xl">{selectedTemplate.icon}</div>
                    <div className="flex-1">
                      <h3 className="text-3xl font-bold text-[#1d1d1f] dark:text-white mb-2">{selectedTemplate.name}</h3>
                      <div className="flex items-center gap-3 mb-3">
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getCategoryColor(selectedTemplate.category)}`}>
                          ⚡ Saves {selectedTemplate.timeSaved}
                        </span>
                        <span className="px-3 py-1 rounded-full text-sm font-semibold bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400">
                          ⏱️ {selectedTemplate.duration} minutes
                        </span>
                      </div>
                      <p className="text-base text-[#1d1d1f] dark:text-white font-medium">{selectedTemplate.description}</p>
                    </div>
                  </div>
                </div>

                {/* Benefits */}
                <div className="bg-white dark:bg-[#1c1c1e] p-5 rounded-xl border border-[#d2d2d7] dark:border-[#2c2c2e]">
                  <h4 className="text-sm font-bold text-[#86868b] dark:text-[#8e8e93] uppercase tracking-wider mb-3 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    What You'll Achieve
                  </h4>
                  <div className="space-y-2">
                    {selectedTemplate.benefits.map((benefit, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <svg className="w-5 h-5 text-green-600 dark:text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-sm text-[#1d1d1f] dark:text-white font-medium">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Structured Agenda */}
                <div className="bg-white dark:bg-[#1c1c1e] p-5 rounded-xl border border-[#d2d2d7] dark:border-[#2c2c2e]">
                  <h4 className="text-sm font-bold text-[#86868b] dark:text-[#8e8e93] uppercase tracking-wider mb-4 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                    Your Step-by-Step Agenda
                  </h4>
                  <div className="space-y-3">
                    {selectedTemplate.agenda.map((item, index) => (
                      <div key={index} className="flex items-start gap-3 p-3 bg-gradient-to-r from-blue-50 to-transparent dark:from-blue-900/10 dark:to-transparent rounded-lg border-l-4 border-[#06c]">
                        <div className="flex items-center justify-center w-7 h-7 rounded-full bg-[#06c] text-white text-sm font-bold flex-shrink-0">
                          {index + 1}
                        </div>
                        <div className="text-sm text-[#1d1d1f] dark:text-white font-medium pt-0.5">{item}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Pro Tip */}
                <div className="p-5 bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-xl border-2 border-yellow-200 dark:border-yellow-800/50">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">💡</div>
                    <div className="flex-1">
                      <div className="font-bold text-[#1d1d1f] dark:text-white mb-2 flex items-center gap-2">
                        Pro Tip for Maximum Efficiency
                      </div>
                      <div className="text-sm text-[#1d1d1f] dark:text-white leading-relaxed">{selectedTemplate.tips}</div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-[#86868b] dark:text-[#8e8e93]">
                <div className="text-center">
                  <div className="text-7xl mb-4">📋</div>
                  <p className="text-xl font-semibold mb-2">Choose a template</p>
                  <p className="text-sm">See how structured meetings save you time</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-[#d2d2d7] dark:border-[#2c2c2e] bg-[#f5f5f7] dark:bg-[#0d0d0d] flex items-center justify-between">
          <div className="text-sm text-[#86868b] dark:text-[#8e8e93]">
            {selectedTemplate && (
              <span className="font-medium">
                ⚡ This template will save you approximately <span className="text-[#06c] font-bold">{selectedTemplate.timeSaved}</span> per meeting
              </span>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-6 py-3 bg-white dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white rounded-lg font-medium hover:bg-[#e8e8ed] dark:hover:bg-[#3c3c3e] transition border border-[#d2d2d7] dark:border-[#2c2c2e]"
            >
              Cancel
            </button>
            <button
              onClick={handleUseTemplate}
              disabled={!selectedTemplate}
              className="px-8 py-3 bg-gradient-to-r from-[#06c] to-[#0055d4] text-white rounded-lg font-semibold hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105"
            >
              Use This Template →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
